import { Component, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { cloneDeep } from 'lodash';
import { firstValueFrom, Subject } from 'rxjs';
import { map, skip, takeUntil } from 'rxjs/operators';
import { ImportStudentListComponent } from '../../../../admin/overlay-container/visibility-room/import-student-list/import-student-list.component';
import { StudentList } from '../../../../models/StudentList';
import { ROLES, User } from '../../../../models';
import { FeatureFlagService, FLAGS } from '../../../../services/feature-flag.service';
import { UserService } from '../../../../services/user.service';

interface UpdateGroupPayload {
	users: number[];
	title: string;
}

interface CreateGroupPayload {
	title: string;
	users: number[];
}

@Component({
	selector: 'app-groups-settings',
	templateUrl: './groups-settings.component.html',
	styleUrls: ['./groups-settings.component.scss'],
})
export class GroupsSettingsComponent implements OnInit, OnDestroy {
	@ViewChild('studentSearchInput') studentSearchInput!: { reset: () => void };
	@ViewChild('groupTitleInput') groupTitleInput!: { nativeElement: { value: string } };

	@Input() canEdit = true;
	@Input() editMode = false;
	@Input() createMode = false;

	groups: StudentList[] = [];
	filteredGroups: StudentList[] = [];
	selectedGroup: StudentList | undefined;
	studentsInSelectedGroup: User[] = [];
	filteredStudents: User[] = [];
	groupTitle = '';
	searchInProgress = false;
	importStudentListDialogRef!: MatDialogRef<ImportStudentListComponent>;
	hasSchedulesFF = false;
	destroy$: Subject<void> = new Subject<void>();

	constructor(
		private userService: UserService,
		private featureFlags: FeatureFlagService,
		public dialog: MatDialog,
		@Inject(MAT_DIALOG_DATA) public data: { groupToEdit: StudentList }
	) {}

	ngOnInit(): void {
		this.hasSchedulesFF = this.featureFlags.isFeatureEnabledV2(FLAGS.Schedules);
		this.userService.getStudentGroups();
		this.userService.studentGroups$.pipe(takeUntil(this.destroy$)).subscribe((groups: StudentList[]) => {
			this.groups = groups;
			this.filteredGroups = groups;
		});
		this.handleGroupSelect(this.data?.groupToEdit);
	}

	handleGroupSelect(group: StudentList) {
		if (this.selectedGroup == group) {
			this.selectedGroup = undefined;
			this.studentsInSelectedGroup = [];
			this.filteredStudents = [];
			this.groupTitle = '';
		} else {
			this.selectedGroup = group;
			this.studentsInSelectedGroup = group.users;
			this.filteredStudents = group.users;
			this.groupTitle = group.title;
		}
		this.editMode = false;
		this.createMode = false;
	}

	handleStudentSelect(item: User) {
		if (this.studentsInSelectedGroup?.length) {
			this.studentsInSelectedGroup.push(item);
		} else {
			this.studentsInSelectedGroup = [item];
		}
		this.studentSearchInput.reset();
	}

	removeGroup() {
		this.userService.deleteStudentGroupRequest(this.selectedGroup?.id).pipe(takeUntil(this.destroy$)).subscribe();
		this.selectedGroup = undefined;
		this.filteredStudents = [];
		this.studentsInSelectedGroup = [];
	}

	editGroup() {
		this.editMode = true;
		this.filteredStudents = [];
	}

	updateGroup() {
		const studentsCopy = cloneDeep(this.studentsInSelectedGroup);
		const groupPayload: UpdateGroupPayload = {
			users: studentsCopy.map((student) => student.id),
			title: this.groupTitleInput.nativeElement.value,
		};
		this.userService
			.updateStudentGroupRequest(this.selectedGroup?.id, groupPayload)
			.pipe(takeUntil(this.destroy$))
			.subscribe((group) => {
				this.filteredStudents = group.users;
				this.editMode = false;
			});
	}

	createModeOn() {
		this.createMode = true;
		this.groupTitle = 'New Student Group';
		this.filteredStudents = [];
		this.studentsInSelectedGroup = [];
	}

	createModeOff() {
		this.createMode = false;
		this.groupTitle = '';
		this.filteredStudents = [];
		this.studentsInSelectedGroup = [];
	}

	createGroup() {
		const newGroupPayload: CreateGroupPayload = {
			title: this.groupTitleInput.nativeElement.value,
			users: this.studentsInSelectedGroup.map((student) => student.id),
		};

		this.userService
			.createStudentGroupRequest(newGroupPayload)
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => (this.createMode = false));
	}

	onGroupSearch(search: string) {
		if (search !== '') {
			this.filteredGroups = this.groups.filter((group) => {
				const titleMatches = group.title.toLowerCase().includes(search.toLowerCase());
				return titleMatches;
			});
		} else {
			this.filteredGroups = this.groups;
		}
	}

	onStudentInGroupSearch(search: string) {
		if (search !== '') {
			this.filteredStudents = this.studentsInSelectedGroup.filter((student) => {
				const nameMatches = student.display_name.toLowerCase().includes(search.toLowerCase());
				const emailMatches = student.primary_email.toLowerCase().includes(search.toLowerCase());
				return nameMatches || emailMatches;
			});
		} else {
			this.filteredStudents = this.studentsInSelectedGroup;
		}
	}

	onStudentSearch(search: string) {
		if (search !== '') {
			this.userService
				.getAccountsRoles(ROLES.Student, search, 100)
				?.pipe(
					skip(1),
					takeUntil(this.destroy$),
					map((students: User[]) => {
						return students.filter((student) => !this.studentsInSelectedGroup.some((selectedStudent) => selectedStudent.id === student.id));
					})
				)
				.subscribe((filteredStudents) => (this.filteredStudents = filteredStudents));
			this.searchInProgress = true;
		} else {
			this.filteredStudents = [];
			this.searchInProgress = false;
		}
	}

	removeSelection(selection: User) {
		this.studentsInSelectedGroup = this.studentsInSelectedGroup.filter((s) => s !== selection);
	}

	async showImportStudentList(): Promise<void> {
		const dialogRef = (this.importStudentListDialogRef = this.dialog.open(ImportStudentListComponent, {
			closeOnNavigation: true,
			panelClass: 'main-form-dialog-container',
			backdropClass: 'custom-backdrop',
			maxWidth: '100vw',
			data: {
				forInput: true,
			},
		}));
		const students = await firstValueFrom(dialogRef.afterClosed());
		if (students && students.length > 0) {
			this.studentsInSelectedGroup = this.studentsInSelectedGroup.concat(students);
			//filter out duplicate imports by user id
			const idMap = new Map(this.studentsInSelectedGroup.map((user) => [user.id, user]));
			this.studentsInSelectedGroup = Array.from(idMap.values());
			this.studentSearchInput.reset();
			this.searchInProgress = false;
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
